/**
 * Featured Categories / Collections band — shared types.
 *
 * Templates fix the crop shape from count alone (Hero → Quad). Creators pick
 * slots and images; they never compose the row layout.
 */

export type FeaturedBandLayout = "card" | "tile";

export type FeaturedBandImageMode = "single" | "grid";

export type FeaturedBandImage = {
  /** Product / upload URL. Empty → placeholder. */
  src: string;
  alt?: string;
  /** Overlay label (e.g. brand name on Featured brands grid cells). */
  label?: string;
  /** Optional brand slug for per-cell navigation. */
  brandSlug?: string;
  /** Focal point for uploads (0–100). Defaults to center. */
  x?: number;
  y?: number;
  zoom?: number;
  /** Product shots contain; uploads cover. */
  fit?: "contain" | "cover";
};

export type FeaturedBandSlot = {
  id: string;
  /** Category or collection display name. */
  title: string;
  /** Deep-link slug (shop category or carousel). */
  slug?: string;
  mode: FeaturedBandImageMode;
  images: FeaturedBandImage[];
  /** Optional product count for editor chrome. */
  count?: number;
};

export type FeaturedBandTemplate = {
  name: string;
  hint: string;
  layout: FeaturedBandLayout;
  /** Max images when slot.mode === "grid". */
  maxImages: number;
};

export const FEATURED_BAND_TEMPLATES: Record<1 | 2 | 3 | 4, FeaturedBandTemplate> = {
  1: { name: "Hero", hint: "One card, full width", layout: "card", maxImages: 3 },
  2: { name: "Split", hint: "Two cards side by side", layout: "card", maxImages: 4 },
  3: { name: "Trio", hint: "Three squares, name below", layout: "tile", maxImages: 4 },
  4: { name: "Quad", hint: "Four squares, name below", layout: "tile", maxImages: 4 },
};

export function featuredBandTemplateForCount(
  count: number,
): FeaturedBandTemplate {
  const n = Math.min(4, Math.max(1, Math.round(count))) as 1 | 2 | 3 | 4;
  return FEATURED_BAND_TEMPLATES[n];
}

export function normalizeFeaturedBandCount(count: unknown): 1 | 2 | 3 | 4 {
  const n = Number(count);
  if (n === 1 || n === 2 || n === 3 || n === 4) return n;
  // Corrupt saves (e.g. count=5 from a 5-slug selectedSlugs write) must clamp
  // to Quad — not collapse to Hero.
  if (Number.isFinite(n) && n > 4) return 4;
  return 1;
}

/** Featured Collections: Hero → Trio only (Quad removed per product feedback). */
export function normalizeFeaturedCollectionCount(count: unknown): 1 | 2 | 3 {
  const n = normalizeFeaturedBandCount(count);
  return n === 4 ? 3 : (n as 1 | 2 | 3);
}

export const FEATURED_COLLECTION_COUNTS = [1, 2, 3] as const;
export const FEATURED_CATEGORY_COUNTS = [1, 2, 3, 4] as const;
/** Category / collection display names on the band. */
export const FEATURED_BAND_TITLE_MAX = 100;

export function clipFeaturedBandTitle(value: string): string {
  return String(value || "").slice(0, FEATURED_BAND_TITLE_MAX);
}
